// @ts-check
// "Agendar" da campanha de e-mail (spec-2.0.0 K13; spec/email.md seção 10.2): dia e horário de 5 em 5 minutos no fuso
// do membro, de agora mais 10 minutos até agora mais 90 dias; o servidor confere de novo (422 SCHEDULE_INVALID) e o
// cron de 5 minutos começa o envio por volta do horário.
import { html } from 'htm/preact';
import { useMemo, useState } from 'preact/hooks';
import { ApiError } from '../../data/api-client.js';
import { scheduleEmailCampaign } from '../../data/email.js';
import { defaultScheduleChoice, readScheduleChoice, scheduleDateBounds, scheduleTimeOptions } from '../../lib/email-campaigns.js';
import { formatDateTime, getFormatPreferences } from '../../lib/format.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import { timeZoneNameInSentence } from '../../../../shared/time/time-zone-names.js';

/**
 * @param {{ campaignId: string, onClose: () => void, onScheduled: (campaign: import('../../data/email.js').EmailCampaign) => void,
 *   beforeSchedule?: () => Promise<unknown> }} props
 */
export function EmailCampaignScheduleDialog({ campaignId, onClose, onScheduled, beforeSchedule }) {
  const timeZone = getFormatPreferences().timeZone;
  const [choice, setChoice] = useState(() => defaultScheduleChoice(Date.now(), timeZone));
  const [problem, setProblem] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const bounds = useMemo(() => scheduleDateBounds(Date.now(), timeZone), [timeZone]);
  const times = useMemo(() => scheduleTimeOptions(), []);

  const confirm = async () => {
    const read = readScheduleChoice(choice, timeZone, Date.now());
    if (!read.scheduledAt) {
      setProblem(t(`emailCampaigns.send.scheduleProblems.${read.problem}`));
      return;
    }
    setIsSaving(true);
    try {
      if (beforeSchedule) await beforeSchedule();
      const campaign = await scheduleEmailCampaign(campaignId, read.scheduledAt);
      showSuccessSnackbar(t('emailCampaigns.toasts.scheduled', { date: formatDateTime(campaign.scheduledAt ?? read.scheduledAt) }));
      onScheduled(campaign);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'SCHEDULE_INVALID' && typeof error.details?.reason === 'string'
        && ['TOO_SOON', 'TOO_FAR'].includes(error.details.reason)) setProblem(t(`emailCampaigns.send.scheduleProblems.${error.details.reason}`));
      else showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${t('emailCampaigns.send.scheduleTitle')} subtitle=${t('emailCampaigns.send.scheduleHint', { timeZone: timeZoneNameInSentence(timeZone) })}
    confirmLabel=${t('emailCampaigns.send.confirm')} isLoading=${isSaving} onClose=${onClose} onConfirm=${confirm}>
    <div class="ec-schedule-fields" data-testid="email-campaign-schedule">
      <label class="ec-field">
        <span class="ec-field-label">${t('emailCampaigns.send.scheduleDate')}</span>
        <input class="text-input" type="date" value=${choice.date} min=${bounds.min} max=${bounds.max}
          onInput=${(/** @type {Event} */ event) => {
            setProblem(null);
            setChoice((current) => ({ ...current, date: /** @type {HTMLInputElement} */ (event.currentTarget).value }));
          }} />
      </label>
      <label class="ec-field">
        <span class="ec-field-label">${t('emailCampaigns.send.scheduleTime')}</span>
        <select class="text-input" value=${choice.time}
          onChange=${(/** @type {Event} */ event) => {
            setProblem(null);
            setChoice((current) => ({ ...current, time: /** @type {HTMLSelectElement} */ (event.currentTarget).value }));
          }}>
          ${times.map((time) => html`<option key=${time} value=${time}>${time}</option>`)}
        </select>
      </label>
    </div>
    ${problem ? html`<p class="input-hint" data-danger="true" role="alert">${problem}</p>` : null}
    <p class="input-hint">${t('emailCampaigns.send.warmupNote')}</p>
  <//>`;
}
